import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as emailAuth from "@/services/auth/email-auth";
import { registerApiClient } from "@/services/api-client";

const EMAIL_STORAGE_KEY = "pokegram_email";
const REFRESH_TOKEN_STORAGE_KEY = "pokegram_refresh_token";

type AuthState =
  | { status: "idle" | "loading" }
  | { status: "needs_login" }
  | {
      status: "authenticated";
      user: {
        player_id?: string;
        username?: string;
      };
      accessToken: string;
      expiresAt: number;
    }
  | { status: "error"; error: string };

type ActionResult = { ok: boolean; message?: string };

type AuthContextValue = {
  state: AuthState;
  reload: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => string | null;
  applyAccessToken: (accessToken: string) => void;
  loginWithEmail: (email: string, password: string) => Promise<ActionResult>;
  signupWithEmail: (
    email: string,
    password: string,
  ) => Promise<ActionResult & { confirmationRequired?: boolean }>;
  confirmEmailSignup: (email: string, code: string) => Promise<ActionResult>;
  forgotPassword: (email: string) => Promise<ActionResult>;
  confirmForgotPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<ActionResult>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwtPayload(accessToken: string): {
  player_id?: string;
  username?: string;
  exp?: number;
} {
  const payloadBase64 = accessToken.split(".")[1]!;
  return JSON.parse(atob(payloadBase64));
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>({ status: "idle" });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef<boolean>(false);
  const stateRef = useRef<AuthState>(state);
  stateRef.current = state;

  const clearEmailSession = () => {
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  };

  const scheduleTokenRefresh = (expiresIn: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const refreshIn = Math.max(expiresIn - 600, expiresIn / 2);

    refreshTimerRef.current = setTimeout(() => {
      void refreshEmailSession(true);
    }, refreshIn * 1000);
  };

  const applyAuthenticatedState = (accessToken: string, expiresIn: number) => {
    const payload = decodeJwtPayload(accessToken);
    const nowSec = Math.floor(Date.now() / 1000);
    const expiresAt = nowSec + expiresIn;

    setState({
      status: "authenticated",
      user: {
        ...(payload.player_id ? { player_id: payload.player_id } : {}),
        ...(payload.username ? { username: payload.username } : {}),
      },
      accessToken,
      expiresAt,
    });

    scheduleTokenRefresh(expiresIn);
  };

  const applyAccessToken = (accessToken: string) => {
    const payload = decodeJwtPayload(accessToken);
    const nowSec = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp ? payload.exp - nowSec : 900;
    applyAuthenticatedState(accessToken, expiresIn);
  };

  const refreshEmailSession = async (isBackground = false): Promise<boolean> => {
    if (isBackground && isRefreshingRef.current) return false;

    const email = localStorage.getItem(EMAIL_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!email || !refreshToken) return false;

    if (isBackground) isRefreshingRef.current = true;

    try {
      const json = await emailAuth.refresh({
        email,
        refresh_token: refreshToken,
      });

      if (!json.ok || !json.access_token) {
        return false;
      }

      applyAuthenticatedState(json.access_token, json.expires_in || 900);
      return true;
    } catch (e) {
      console.error("[Auth] Email session refresh failed:", e);
      if (isBackground) scheduleTokenRefresh(30);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const restoreEmailSessionOrPromptLogin = async () => {
    if (import.meta.env.DEV) {
      const { isOfflineMode, OFFLINE_ACCESS_TOKEN, OFFLINE_PLAYER_ID } = await import(
        "@/dev/offline-mode"
      );
      if (isOfflineMode()) {
        const { offlinePlayer } = await import("@/dev/offline-player");
        setState({
          status: "authenticated",
          user: {
            player_id: OFFLINE_PLAYER_ID,
            username: offlinePlayer.username,
          },
          accessToken: OFFLINE_ACCESS_TOKEN,
          expiresAt: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365,
        });
        return;
      }
    }

    const email = localStorage.getItem(EMAIL_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!email || !refreshToken) {
      setState({ status: "needs_login" });
      return;
    }

    setState({ status: "loading" });
    const restored = await refreshEmailSession();
    if (!restored) {
      clearEmailSession();
      setState({ status: "needs_login" });
    }
  };

  const refreshEmailSessionRef = useRef(refreshEmailSession);
  refreshEmailSessionRef.current = refreshEmailSession;

  useEffect(() => {
    void restoreEmailSessionOrPromptLogin();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerApiClient({
      getAccessToken: () =>
        stateRef.current.status === "authenticated"
          ? stateRef.current.accessToken
          : null,
      onUnauthorized: () => {
        void refreshEmailSessionRef.current(true);
      },
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      reload: restoreEmailSessionOrPromptLogin,
      logout: () => {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        clearEmailSession();
        setState({ status: "idle" });
        if (typeof window !== "undefined") window.location.reload();
      },
      getAccessToken: () => {
        if (state.status !== "authenticated") return null;
        return state.accessToken;
      },
      applyAccessToken,
      loginWithEmail: async (email, password) => {
        const json = await emailAuth.login({ email, password });
        if (!json.ok || !json.access_token || !json.refresh_token) {
          return { ok: false, message: json.message };
        }
        localStorage.setItem(EMAIL_STORAGE_KEY, email);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, json.refresh_token);
        applyAuthenticatedState(json.access_token, json.expires_in || 900);
        return { ok: true };
      },
      signupWithEmail: async (email, password) => {
        const json = await emailAuth.signup({ email, password });
        return json.confirmation_required !== undefined
          ? {
              ok: json.ok,
              message: json.message,
              confirmationRequired: json.confirmation_required,
            }
          : { ok: json.ok, message: json.message };
      },
      confirmEmailSignup: async (email, code) => {
        const json = await emailAuth.confirmSignup({ email, code });
        return { ok: json.ok, message: json.message };
      },
      forgotPassword: async (email) => {
        const json = await emailAuth.forgotPassword({ email });
        return { ok: json.ok, message: json.message };
      },
      confirmForgotPassword: async (email, code, newPassword) => {
        const json = await emailAuth.confirmForgotPassword({
          email,
          code,
          new_password: newPassword,
        });
        return { ok: json.ok, message: json.message };
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export const AuthGate: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
  const { state } = useAuth();

  if (state.status === "loading" || state.status === "idle") {
    return fallback ?? <div style={{ padding: 16 }}>Authenticating…</div>;
  }

  if (state.status === "error" || state.status === "needs_login") {
    return (
      <div style={{ padding: 16 }}>
        <h3>Auth error</h3>
        <p>{state.status === "error" ? state.error : "Sign in required"}</p>
      </div>
    );
  }

  return <>{children}</>;
};
